import React, { useState } from 'react';

// Paged spec renderer. editable mode = visual editor:
// - drag a section, drop into any column/page zone (zones highlight)
// - click texts to edit inline
// - hover a section for size/remove controls; image box has size presets
export default function SpecRender({ schema, values = {}, docTitle = '', placeholder = '—',
  editable = false, onSection = () => {}, onField = () => {}, onRemoveField = () => {},
  onRemoveSection = () => {}, onAddField = () => {}, onMeta = () => {}, onReorder = () => {} }) {

  const [dragIdx, setDragIdx] = useState(null);
  const [overZone, setOverZone] = useState(null); // 'pn-col'
  const [hoverSec, setHoverSec] = useState(null);
  const [overSec, setOverSec] = useState(null);

  const getVal = (key) => {
    const v = values[key];
    if (v === undefined || v === null || v === '') return null;
    return typeof v === 'object' ? v.value : v;
  };
  const getRef = (key) => {
    const v = values[key];
    return v && typeof v === 'object' ? (v.reference || '') : '';
  };
  const show = (key) => getVal(key) ?? placeholder;

  const header = schema.header;
  const sigs = schema.signatures;
  const sections = (schema.sections || []).map((s, i) => ({ ...s, _idx: i }));
  const imgH = schema.image_h || 150;
  const imgW = schema.image_w || 75; // percent

  const cellB = { border: '1px solid #999', padding: '4px 8px', fontSize: 11 };
  const labelCell = { ...cellB, fontWeight: 700, background: '#f7f7f7', width: 140 };

  const EditableText = ({ value, onChange, style }) => editable
    ? <input value={value} onChange={e => onChange(e.target.value)}
        onMouseDown={e => e.stopPropagation()}
        style={{ ...style, border: 'none', borderBottom: '1px dashed #c9a0c0', background: 'transparent', outline: 'none',
                 width: Math.max(60, String(value).length * 7.5) + 'px', fontFamily: 'inherit' }} />
    : <span style={style}>{value}</span>;

  const dropProps = (pn, col) => !editable ? {} : {
    onDragOver: (e) => { e.preventDefault(); setOverZone(pn + '-' + col); },
    onDragLeave: () => setOverZone(z => z === pn + '-' + col ? null : z),
    onDrop: (e) => {
      e.preventDefault();
      if (dragIdx !== null) onSection(dragIdx, { page: pn, column: col });
      setDragIdx(null); setOverZone(null);
    }
  };
  const zoneStyle = (pn, col) => !editable ? {} : {
    outline: overZone === pn + '-' + col ? '2px dashed #7B2D6B' : '2px dashed transparent',
    borderRadius: 6, transition: 'outline 0.1s', minHeight: 40
  };

  const SectionBlock = ({ sec }) => {
    const halves = sec.fields.some(f => f.half);
    const isHover = hoverSec === sec._idx;
    const width = sec.width || 100;

    const hoverBar = editable && isHover ? (
      <div style={{ position: 'absolute', top: -11, right: 0, display: 'flex', gap: 4, background: '#fff',
                    border: '1px solid #ddd', borderRadius: 12, padding: '2px 8px', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', zIndex: 5 }}>
        <button onClick={() => onAddField(sec._idx)} title="Add field"
          style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, cursor: 'pointer', border: '1px solid #ccc', background: '#fff', color: '#555' }}>+ field</button>
        <button onClick={() => window.confirm('Remove "' + sec.title + '"?') && onRemoveSection(sec._idx)}
          style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, cursor: 'pointer', border: '1px solid #e57373', background: '#fff', color: '#c62828' }}>remove</button>
      </div>
    ) : null;

    const FieldX = ({ fi }) => editable && isHover
      ? <button onClick={() => onRemoveField(sec._idx, fi)}
          style={{ border: 'none', background: '#c62828', color: '#fff', borderRadius: '50%', width: 12, height: 12, fontSize: 8,
                   cursor: 'pointer', lineHeight: '10px', padding: 0, marginLeft: 4 }}>×</button>
      : null;

    const body = halves ? (() => {
      const leftF = sec.fields.map((f, fi) => ({ f, fi })).filter(x => x.f.half === 'left');
      const rightF = sec.fields.map((f, fi) => ({ f, fi })).filter(x => x.f.half === 'right');
      const Prose = ({ items }) => (
        <div style={{ padding: '7px 11px', fontSize: 11.5, lineHeight: 1.55 }}>
          {items.map(({ f, fi }) => (
            <div key={f.key}><b><EditableText value={f.label} onChange={v => onField(sec._idx, fi, { label: v })} style={{ fontWeight: 700 }} /><FieldX fi={fi} />:</b> {show(f.key)}</div>
          ))}
        </div>
      );
      return (
        <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', border: '1px solid #999' }}>
          <div style={{ flex: 1, minWidth: 0 }}><Prose items={leftF} /></div>
          <div style={{ flex: 1.3, minWidth: 0, borderLeft: '1px solid #999' }}><Prose items={rightF} /></div>
        </div>
      );
    })() : (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        {sec.layout === 'test_req_ref' && (
          <thead><tr>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left' }}>Test</th>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left' }}>Requirement</th>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left' }}>Reference Standard</th>
          </tr></thead>
        )}
        {sec.layout === 'nutrient_amount_dv' && (
          <thead><tr>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left' }}>Nutrient</th>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left' }}>Amount</th>
            <th style={{ ...cellB, background: '#efefef', textAlign: 'left', width: 60 }}>% DV</th>
          </tr></thead>
        )}
        <tbody>
          {sec.fields.map((f, fi) => (
            <tr key={f.key}>
              <td style={labelCell}><EditableText value={f.label} onChange={v => onField(sec._idx, fi, { label: v })} /><FieldX fi={fi} /></td>
              <td style={cellB}>{show(f.key)}</td>
              {sec.layout === 'test_req_ref' && <td style={{ ...cellB, fontStyle: 'italic', fontSize: 10 }}>{getRef(f.key) || placeholder}</td>}
              {sec.layout === 'nutrient_amount_dv' && <td style={{ ...cellB, width: 60 }}>{getRef(f.key) || placeholder}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    );

    const startResize = (e) => {
      e.preventDefault(); e.stopPropagation();
      const parent = e.currentTarget.parentElement.parentElement; // zone container
      const parentW = parent.getBoundingClientRect().width;
      const move = (ev) => {
        const secEl = e.currentTarget ? null : null;
        const rect = parent.getBoundingClientRect();
        let pct = ((ev.clientX - rect.left) / parentW) * 100;
        pct = Math.max(30, Math.min(100, Math.round(pct)));
        onSection(sec._idx, { width: pct });
      };
      const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
      window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    };
    return (
      <div draggable={editable}
        data-secidx={sec._idx}
        onDragStart={() => setDragIdx(sec._idx)}
        onDragOver={editable ? ((e) => { e.preventDefault(); e.stopPropagation(); setOverSec(sec._idx); }) : undefined}
        onDrop={editable ? ((e) => {
          e.preventDefault(); e.stopPropagation();
          if (dragIdx !== null && dragIdx !== sec._idx) onReorder(dragIdx, sec._idx, { page: sec.page || 0, column: sec.column || 'full' });
          setDragIdx(null); setOverSec(null); setOverZone(null);
        }) : undefined}
        onMouseEnter={() => editable && setHoverSec(sec._idx)}
        onMouseLeave={() => editable && setHoverSec(h => h === sec._idx ? null : h)}
        style={{ marginBottom: 12, width: width + '%', position: 'relative',
                 cursor: editable ? 'grab' : 'default',
                 borderTop: editable && overSec === sec._idx && dragIdx !== null ? '3px solid #7B2D6B' : '3px solid transparent',
                 outline: editable && isHover ? '1px solid #e0c8da' : 'none', borderRadius: 4 }}>
        {editable && isHover && (
          <div onMouseDown={startResize}
            style={{ position: 'absolute', top: 0, right: -4, width: 8, height: '100%', cursor: 'ew-resize', zIndex: 6,
                     background: 'linear-gradient(to right, transparent 40%, #b78ab0 40%, #b78ab0 60%, transparent 60%)' }} />
        )}
        {hoverBar}
        {(!halves || editable) && (
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3 }}>
            <EditableText value={sec.title} onChange={v => onSection(sec._idx, { title: v })} style={{ fontWeight: 700, fontSize: 12 }} />
          </div>
        )}
        {body}
      </div>
    );
  };

  const ImageSlot = () => {
    const startImgResize = (e) => {
      e.preventDefault(); e.stopPropagation();
      const box = e.currentTarget.parentElement;
      const rect = box.getBoundingClientRect();
      const parentW = box.parentElement.getBoundingClientRect().width;
      const move = (ev) => {
        const h = Math.max(60, Math.min(400, Math.round(ev.clientY - rect.top)));
        const w = Math.max(30, Math.min(100, Math.round(((ev.clientX - rect.left) / parentW) * 100)));
        onMeta({ image_h: h, image_w: w });
      };
      const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
      window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    };
    return (
      <div style={{ marginBottom: 12, position: 'relative' }}>
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 3 }}>Product Image</div>
        <div style={{ position: 'relative', width: imgW + '%' }}>
          {values.__product_image_url
            ? <img src={values.__product_image_url} alt="product" style={{ height: imgH, width: '100%', objectFit: 'contain', border: '1px solid #ccc', display: 'block', background: '#fff' }} />
            : <div style={{ border: '1px dashed #bbb', height: imgH, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 11 }}>[ product image ]</div>}
          {editable && (
            <div onMouseDown={startImgResize}
              style={{ position: 'absolute', bottom: -5, right: -5, width: 14, height: 14, cursor: 'nwse-resize',
                       background: '#7B2D6B', borderRadius: 3, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
          )}
        </div>
      </div>
    );
  };

  const HeaderBlock = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, border: '2px solid #333' }}>
      <tbody><tr>
        {header?.logo_url && (
          <td style={{ border: '1px solid #333', padding: 8, width: 130, textAlign: 'center', verticalAlign: 'middle' }}>
            <img src={header.logo_url} alt="logo" style={{ maxWidth: 110, maxHeight: 45 }} />
          </td>
        )}
        <td style={{ border: '1px solid #333', padding: 8, textAlign: 'center', verticalAlign: 'middle' }}>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{header?.company || 'Company Name'}</div>
          <div style={{ fontSize: 12 }}>Product Specification</div>
        </td>
        {(header?.fields || []).length > 0 && (
          <td style={{ border: '1px solid #333', padding: 0, width: 220, verticalAlign: 'top' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {header.fields.map(f => (
                  <tr key={f.key}>
                    <td style={{ border: '1px solid #333', padding: '3px 6px', fontWeight: 700, fontSize: 10, width: 90 }}>{f.label}</td>
                    <td style={{ border: '1px solid #333', padding: '3px 6px', fontSize: 10 }}>{show(f.key)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        )}
      </tr></tbody>
    </table>
  );

  const Signatures = () => sigs && sigs.length > 0 ? (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
      <tbody><tr>
        {sigs.map(s => (
          <td key={s.key} style={{ border: '1px solid #333', padding: '8px 10px', verticalAlign: 'top', width: (100 / sigs.length) + '%' }}>
            <div style={{ fontWeight: 700, fontSize: 11 }}>{s.role}:</div>
            <div style={{ fontSize: 11, fontWeight: 600, minHeight: 14 }}>{getVal('sig_' + s.key) || s.name_hint || ''}</div>
            <div style={{ minHeight: 34 }}>{s.sig_image_url && <img src={s.sig_image_url} alt="" style={{ maxHeight: 32 }} />}</div>
            <div style={{ fontWeight: 700, fontSize: 10.5, borderTop: '1px solid #999', paddingTop: 3 }}>{s.title || s.title_hint || ''}</div>
          </td>
        ))}
      </tr></tbody>
    </table>
  ) : null;

  const basePages = Array.from(new Set(sections.map(s => s.page || 0))).sort((a, b) => a - b);
  if (!basePages.length) basePages.push(0);
  const pageNums = editable ? Array.from(new Set([...basePages, basePages[basePages.length - 1] + 1])) : basePages;
  const lastContent = basePages[basePages.length - 1];

  return (
    <div className="spec-print-area">
      {editable && <div style={{ textAlign: 'center', fontSize: 11, color: '#888', marginBottom: 8 }}>
        ✋ Drag any section and drop it into a column · click text to edit · hover a section for options</div>}
      {pageNums.map(pn => {
        const pageSecs = sections.filter(s => (s.page || 0) === pn);
        const left = pageSecs.filter(s => s.column === 'left');
        const right = pageSecs.filter(s => s.column === 'right');
        const full = pageSecs.filter(s => !s.column || s.column === 'full');
        const empty = pageSecs.length === 0;
        if (empty && !editable) return null;
        return (
          <div key={pn} className="spec-page"
            style={{ background: '#fff', color: '#111', width: 794, minHeight: editable && empty ? 300 : 1123, padding: '30px 34px', margin: '0 auto 18px',
                     border: '1px solid #ccc', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', fontFamily: 'Georgia, "Times New Roman", serif',
                     boxSizing: 'border-box', position: 'relative', opacity: empty ? 0.75 : 1 }}>
            {(!empty || !editable) && <HeaderBlock />}
            {empty && editable && <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginTop: 10 }}>— drop sections here to start page {pn + 1} —</div>}
            {pn === pageNums[0] && docTitle && !empty && <div style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{docTitle}</div>}
            <div {...dropProps(pn, 'full')} style={zoneStyle(pn, 'full')}>
              {full.map(sec => <SectionBlock key={sec.key} sec={sec} />)}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div {...dropProps(pn, 'left')} style={{ flex: 1, minWidth: 0, ...zoneStyle(pn, 'left') }}>
                {left.map(sec => <SectionBlock key={sec.key} sec={sec} />)}
              </div>
              <div {...dropProps(pn, 'right')} style={{ flex: 1, minWidth: 0, ...zoneStyle(pn, 'right') }}>
                {pn === pageNums[0] && schema.has_product_image && !empty && <ImageSlot />}
                {right.map(sec => <SectionBlock key={sec.key} sec={sec} />)}
              </div>
            </div>
            {pn === lastContent && <Signatures />}
            <div style={{ position: 'absolute', bottom: 6, right: 12, fontSize: 10, color: '#bbb' }}>Page {pn + 1}</div>
          </div>
        );
      })}
    </div>
  );
}
